import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import Modal, { Field } from '../../../../../../common/Modal';
import { projectsApi } from '../../../../../../../services/api';
import { useAlert } from '../../../../../../../contexts/AlertContext';
import { EMPLOYEE_ENGAGEMENT_TYPES } from '../../../../../../../constants/employeeEngagementTypes';
import EngagementTypeSelector from '../../../../../../common/EngagementTypeSelector';

/**
 * Modal for editing an employee's engagement type on a project
 *
 * @param {Object} props
 * @param {Boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Object} props.employee - The employee whose engagement is being edited
 * @param {Function} props.onSuccess - Callback when engagement update is successful
 */
const EditEngagementModal = ({
  isOpen,
  onClose,
  employee,
  onSuccess
}) => {
  const { showAlert } = useAlert();
  const [selectedEngagementType, setSelectedEngagementType] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && employee) {
      setSelectedEngagementType(employee.engagementType?.id || '');
    }
  }, [isOpen, employee]);

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      await projectsApi.updateEmployeeProjectEngagement(employee.employeeProjectId, {
        engagementTypeId: selectedEngagementType
      });

      showAlert('Engagement type updated successfully', 'success');
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error updating engagement type:', error);
      showAlert(error.message || 'Error updating engagement type', 'error', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  if (!employee) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Update Engagement"
      onSubmit={handleSubmit}
      submitText={isSubmitting ? 'Updating...' : 'Update Engagement'}
      submitDisabled={isSubmitting}
      submitButtonProps={{
        sx: { backgroundColor: '#2563EB', color: '#FFFFFF', '&:hover': { backgroundColor: '#1E40AF' } }
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Field>
          <Typography variant="subtitle1" gutterBottom>
            Engagement Type
          </Typography>
          <EngagementTypeSelector
            value={selectedEngagementType}
            onChange={setSelectedEngagementType}
            disabled={isSubmitting}
            disabledEngagementTypeCodes={[EMPLOYEE_ENGAGEMENT_TYPES.FULL_TIME]}
            hideLabel
          />
        </Field>
      </Box>
    </Modal>
  );
};

export default EditEngagementModal;
